import React, { useState, useEffect, useContext } from "react";
import {
  fetchCustomerById,
  updateCustomer,
  fetchBloodGroups,
} from "../../services/Navbar/UpdateProfile";
import { UserContext } from "../../context/UserContext";

export default function UpdateProfileModal({
  show,
  onClose,
  onSuccess,
}) {
  const { user, updateUser } = useContext(UserContext);

  const userId =
    user?.id ||
    user?.userId ||
    user?.customerId ||
    user?.CustomerID;

  const [loading, setLoading] = useState(false);
  const [bloodGroups, setBloodGroups] = useState([]);

  // ✅ verify states
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    customerName: "",
    emailId: "",
    phoneNumber: "",
    dob: "",
    bloodGroup: "",
    gender: "",
    uploadPhoto: null,
  });

  const [previewPhoto, setPreviewPhoto] = useState(null);

  // ================= BLOOD GROUP =================
  useEffect(() => {
    const loadBlood = async () => {
      try {
        const res = await fetchBloodGroups();
        setBloodGroups(res || []);
      } catch (err) {
        console.log(err);
      }
    };

    loadBlood();
  }, []);

  // ================= FETCH USER =================
  useEffect(() => {
    if (!show || !userId) return;

    setLoading(true);

    fetchCustomerById(userId)
      .then((data) => {
        console.log("📦 USER DATA:", data);

        setFormData({
          id: data?.id || userId || "",
          customerName: data?.customerName || "",
          emailId: data?.emailId || "",
          phoneNumber: data?.phoneNumber || "",
          dob: data?.dob || "",
          bloodGroup: data?.bloodGroup || "",
          gender: data?.gender || "",
          uploadPhoto: null,
        });

        setPreviewPhoto(data?.photo || null);

        setIsEmailVerified(false);
        setIsPhoneVerified(false);
      })
      .catch((err) => console.log("FETCH ERROR:", err))
      .finally(() => setLoading(false));
  }, [show, userId]);

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    const { name, value } = e.target;

    // ✅ Email direct edit block
    if (name === "emailId" && !isEmailVerified) {
      return;
    }

    // ✅ Phone direct edit block
    if (name === "phoneNumber" && !isPhoneVerified) {
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // ================= PHOTO =================
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];

    setFormData({
      ...formData,
      uploadPhoto: file,
    });

    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setPreviewPhoto(reader.result);
      };

      reader.readAsDataURL(file);
    }
  };

  // ================= VERIFY =================
  const handleVerifyEmail = () => {
    setIsEmailVerified(true);
  };

  const handleVerifyPhone = () => {
    setIsPhoneVerified(true);
  };

  // ================= UPDATE =================
  const handleUpdate = async () => {
    try {
      setLoading(true);

      console.log("🚀 FORM DATA BEFORE SEND:", formData);

      if (!formData.customerName?.trim()) {
        alert("Customer Name required");
        return;
      }

      if (!formData.phoneNumber?.trim()) {
        alert("Phone Number required");
        return;
      }

      if (!formData.emailId?.trim()) {
        alert("Email required");
        return;
      }

      const fd = new FormData();

      fd.append("Id", String(formData.id || userId || ""));
      fd.append("CustomerName", formData.customerName.trim());
      fd.append("EmailId", formData.emailId.trim());
      fd.append("PhoneNumber", formData.phoneNumber.trim());

      fd.append(
        "DOB",
        formData.dob ? formData.dob.split("T")[0] : ""
      );

      fd.append(
        "BloodGroup",
        String(formData.bloodGroup || 0)
      );

      fd.append(
        "Gender",
        String(formData.gender || 0)
      );

      if (formData.uploadPhoto instanceof File) {
        fd.append("UploadPhoto", formData.uploadPhoto);
      }

      for (let pair of fd.entries()) {
        console.log("📤 FORM:", pair[0], pair[1]);
      }

      const res = await updateCustomer(fd);

      console.log("📦 UPDATE RESPONSE:", res);

      const updatedData =
        res?.data?.data ||
        res?.data ||
        res;

      console.log("🔥 FINAL UPDATED DATA 👉", updatedData);

      // ✅ Context update
      updateUser({
        ...updatedData,
        photo: updatedData?.photo || previewPhoto,
      });

      // ✅ callback
      if (onSuccess) {
        onSuccess({
          customerName:
            updatedData.CustomerName ||
            updatedData.customerName,

          emailId:
            updatedData.EmailId ||
            updatedData.emailId,

          phoneNumber:
            updatedData.PhoneNumber ||
            updatedData.phoneNumber,

          photo:
            updatedData.photo ||
            updatedData.UploadPhoto ||
            previewPhoto,
        });
      }

      alert("Profile updated successfully");

      onClose();

    } catch (err) {
      console.error("❌ UPDATE ERROR:", err);
      alert("Update failed (check console)");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal d-block"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content p-3">

          {/* HEADER */}
          <div className="modal-header">
            <h5>Update Profile</h5>

            <button
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          {/* BODY */}
          <div className="modal-body">

            {/* NAME */}
            <input
              name="customerName"
              className="form-control mb-2"
              value={formData.customerName}
              onChange={handleChange}
              placeholder="Name"
            />

            {/* EMAIL */}
            <div className="d-flex gap-2 mb-2">
              <input
                name="emailId"
                className="form-control"
                value={formData.emailId}
                onChange={handleChange}
                placeholder="Email"
                readOnly={!isEmailVerified}
              />

              <button
                type="button"
                className="btn btn-primary border-0 shadow-none"
                style={{
                  backgroundColor: "#0d6efd",
                  boxShadow: "none",
                  outline: "none",
                  minWidth: "90px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#0d6efd";
                  e.target.style.boxShadow = "none";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#0d6efd";
                  e.target.style.boxShadow = "none";
                }}
                onClick={handleVerifyEmail}
              >
                Verify
              </button>
            </div>

            {/* PHONE */}
            <div className="d-flex gap-2 mb-2">
              <input
                name="phoneNumber"
                className="form-control"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Phone"
                readOnly={!isPhoneVerified}
              />

              <button
                type="button"
                className="btn btn-primary border-0 shadow-none"
                style={{
                  backgroundColor: "#0d6efd",
                  boxShadow: "none",
                  outline: "none",
                  minWidth: "90px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#0d6efd";
                  e.target.style.boxShadow = "none";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#0d6efd";
                  e.target.style.boxShadow = "none";
                }}
                onClick={handleVerifyPhone}
              >
                Verify
              </button>
            </div>

            {/* DOB */}
            <input
              type="date"
              name="dob"
              className="form-control mb-2"
              value={formData.dob?.split("T")[0] || ""}
              onChange={handleChange}
            />

            {/* BLOOD GROUP */}
            <select
              name="bloodGroup"
              className="form-control mb-2"
              value={formData.bloodGroup}
              onChange={handleChange}
            >
              <option value="">
                Select Blood Group
              </option>

              {bloodGroups.map((b) => (
                <option
                  key={b.id}
                  value={b.id}
                >
                  {b.name}
                </option>
              ))}
            </select>

            {/* GENDER */}
            <select
              name="gender"
              className="form-control mb-2"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value="">
                Gender
              </option>

              <option value="1">
                Male
              </option>

              <option value="2">
                Female
              </option>

              <option value="3">
                Other
              </option>
            </select>

            {/* PHOTO */}
            <input
              type="file"
              className="form-control mb-2"
              onChange={handlePhotoChange}
            />

            {/* PREVIEW */}
            {previewPhoto && (
              <img
                src={previewPhoto}
                alt="preview"
                style={{
                  width: 120,
                  height: 120,
                  objectFit: "cover",
                }}
                className="rounded"
              />
            )}
          </div>

          {/* FOOTER */}
          <div className="modal-footer">

            <button
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              className="btn btn-success"
              onClick={handleUpdate}
              disabled={loading}
            >
              {loading
                ? "Updating..."
                : "Update"}
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}